/* Build-in hooks */
import { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

/* Constants */
import { GENRES_LIST, Timespan } from "../../constants/commonConstants";

/* Components */
import { useThunk } from "../../hooks/useThunk";
import { useInput } from "../../hooks/use-input";
import {
    fetchCharts,
    fetchChartManipulations,
    addTrackToChart,
    deleteTrackToChart,
} from "../../services/thunks/TrackThunks";
import { getBasicInfoAllTracks, searchPlaylistTracks } from "../../services/thunks/playlistThunks";

import TrackRow from "../../components/Track/TrackRow";
import ButtonCollection, {
    ButtonOfCollection,
} from "../../components/Inputs/ButtonsCollection";
import DropDown, { DropdownItem } from "../../components/Inputs/DropDown";
import CountryDropdown from "../../components/Form/CountryDropdown";
import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import TrackChartSelectorModal from "../../components/Modals/TrackChartSelectorModal";
import showToast from "../../utils/toastNotifications";
import {
    validateChartTimespan,
    validateChartRank,
} from "../../utils/validationUtils";

const Charts = () => {
    const location = useLocation();
    /* Local constants */
    const TIMESPANS = [7, 30, 90, 365];

    const trackListRef = useRef(null);

    /* Local states */
    const [timespan, setTimespan] = useState(TIMESPANS[0]);
    const [selectedGenre, setSelectedGenre] = useState(GENRES_LIST[0]);
    const [chartTracks, setChartTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openedTrack, setOpenedTrack] = useState(null);
    const [showTrackSelector, setShowTrackSelector] = useState(false);
    const [assignedTracks, setAssignedTracks] = useState(null);
    const [assignedTrackIds, setAssignedTrackIds] = useState([]);
    const [manipulationByTrack, setManipulationByTrack] = useState({});

    //thunks
    const [doFetchCharts, isFetchingTracks] = useThunk(fetchCharts);
    const [doGetTracksPaginated] = useThunk(getBasicInfoAllTracks);
    const [doSearchTracksPaginated] = useThunk(searchPlaylistTracks);
    const [doFetchChartManipulations] = useThunk(fetchChartManipulations);
    const [doAddTrackToChart, isAddingTrack] = useThunk(addTrackToChart);
    const [doDeleteTrackToChart, isDeletingTrack] = useThunk(deleteTrackToChart);

    const { value: timespan1, handleInputChange: handleTimespan1Change, handleInputBlur: handleTimespan1Blur, hasError: timespan1HasError, setValue: setTimespan1, errorMessage: timespan1ErrorMessage, } = useInput("", validateChartTimespan);

    const {
        value: rank,
        handleInputChange: handleRankChange,
        handleInputBlur: handleRankBlur,
        hasError: rankHasError,
        errorMessage: rankErrorMessage,
        setValue: setRank,
        reset: resetRank,
    } = useInput("", validateChartRank);

    // Set initial values if directed back from Add Tracks form
    useEffect(() => {
        if (location.state?.formData) {
            // setPlaylistDetails(location.state.formData);
            setAssignedTracks(location.state.assignedTracks || []);
        }
    }, []);

    //fetch chart tracks and assigned track IDs
    useEffect(() => {
        fetchAssignedTracks();
    }, [selectedGenre, timespan, timespan1, timespan1HasError]);

    useEffect(() => {
        fetchChartss();
    }, [timespan, timespan1HasError]);

    const fetchChartss = async () => {
        setLoading(true);
        const genre =
            selectedGenre.name === "All Genres"
                ? ""
                : selectedGenre.name === "Hip-Hop / R&B"
                    ? encodeURIComponent(selectedGenre.name)
                    : selectedGenre.name;
        const result = await doFetchCharts({ genre, period: timespan });
        if (result.success) {
            console.log("chart tracks", result?.response?.data);
            setChartTracks(result?.response?.data || []);
        } else {
            setChartTracks([]); // avoid stale data on errors
        }
        setLoading(false);
    };

    const fetchAssignedTracks = async () => {
        const genre =
            selectedGenre.name === "All Genres" ? "" : selectedGenre.name;
        const inputValid =
            !timespan1HasError && String(timespan1 || "").trim() !== "";
        const effectiveTimespan = inputValid ? Number(timespan1) : timespan;
        //console.log("effectiveTimespan", effectiveTimespan);
        const result = await doFetchChartManipulations({
            timespan: effectiveTimespan.toString(),
            genre,
        });

        // console.log("resultttt", result);
        if (result.success) {
            const list = Array.isArray(result?.response?.data)
                ? result.response.data
                : [];
            const assignedTrackIdss = list
                .map((m) => ({
                    trackId: m?.trackId?._id || m?.trackId,
                    editId: m?.editId?._id || m?.editId,
                }))
                .filter((item) => item.trackId && item.editId);
            setAssignedTrackIds(assignedTrackIdss);
            const idMap = {};
            list.forEach((m) => {
                const tId = m?.trackId?._id || m?.trackId;
                const eId = m?.editId?._id || m?.editId;
                const mId = m?._id;
                if (tId && eId && mId) idMap[`${tId}:${eId}`] = mId;
            });
            setManipulationByTrack(idMap);
        }
    };

    const handleTimespanChange = (newTimespan) => {
        setTimespan(newTimespan);
        // scrollToTracks();
    };

    // console.log("manipulationByTrack", manipulationByTrack)

    const handleRemoveFromChart = async (trackId, editId) => {
        const key = `${trackId}:${editId}`;
        console.log("trackid", trackId);
        console.log("editId", editId);
        const manipulationId = manipulationByTrack?.[key];
        if (!manipulationId) {
            setAssignedTracks(null);
            return;
        }
        try {
            const result = await doDeleteTrackToChart({ manipulationId });
            if (result?.success) {
                showToast("success", "Track removed from chart manipulation");
                const inputValid =
                    !timespan1HasError && String(timespan1 || "").trim() !== "";
                const effectiveTimespan = inputValid
                    ? String(Number(timespan1))
                    : String(timespan);
                // const genre = selectedGenre.name === "All Genres" ? "" : selectedGenre.name;
                const refresh = await doFetchChartManipulations({
                    timespan: effectiveTimespan,
                });
                if (refresh?.success) {
                    const list = Array.isArray(refresh?.response?.data)
                        ? refresh.response.data
                        : [];
                    const assignedTrackIdss = list
                        .map((m) => ({
                            trackId: m?.trackId?._id || m?.trackId,
                            editId: m?.editId?._id || m?.editId,
                        }))
                        .filter((item) => item.trackId && item.editId);
                    setAssignedTrackIds(assignedTrackIdss);
                    const idMap = {};
                    list.forEach((m) => {
                        const tId = m?.trackId?._id || m?.trackId;
                        const eId = m?.editId?._id || m?.editId;
                        const mId = m?._id;
                        if (tId && eId && mId) idMap[`${tId}:${eId}`] = mId;
                    });
                    setManipulationByTrack(idMap);
                }
                fetchChartss();
                setAssignedTracks(null);
            } else {
                const msg =
                    result?.error?.message || "Failed to remove track from chart";
                showToast("error", msg);
            }
        } catch (e) {
            showToast("error", e?.message || "Failed to remove track from chart");
        }
    };

    /* Make up all genres according to color codes grouping them */
    const groupedGenres = Object.values(
        GENRES_LIST.reduce((acc, genre) => {
            const code = genre.code.toLowerCase();

            if (!acc[code]) {
                acc[code] = {
                    id: genre.id,
                    name: genre.name,
                    code: code,
                };
            } else {
                acc[code].name += ` / ${genre.name}`;
            }

            return acc;
        }, {})
    );

    // Handle paginated track loading for modal
    const handleLoadTrackPage = async (pageSize, pageNum) => {
        try {
            const result = await doGetTracksPaginated({
                page: pageNum,
                limit: pageSize,
            });

            if (result.success && result.response?.data) {
                const data = result.response.data;
                return {
                    tracks: Array.isArray(data.tracks) ? data.tracks : [],
                    total: data.total || 0,
                    hasMore: data.hasMore ?? false,
                };
            }

            return { tracks: [], total: 0, hasMore: false };
        } catch (error) {
            console.error("Error loading tracks:", error);
            return { tracks: [], total: 0, hasMore: false };
        }
    };

    // Handle search in modal
    const handleSearchTracks = async (searchTerm, pageSize, pageNum) => {
        try {
            const result = await doSearchTracksPaginated({
                search: searchTerm,
                page: pageNum,
                limit: pageSize,
            });

            if (!result.success) {
                throw new Error("Search failed");
            }

            const data = result.response.data || result.response;

            return {
                tracks: Array.isArray(data.tracks) ? data.tracks : [],
                total: data.total || 0,
                hasMore: data.hasMore ?? false,
            };
        } catch (error) {
            console.error("Error searching tracks:", error);
            return { tracks: [], total: 0, hasMore: false };
        }
    };

    const handleTrackSelect = (track) => {
        setAssignedTracks(track);
        showToast("success", `Track \"${track.trackTitle}\" selected`);
    };

    //console.log("defrregrtgrtr", assignedTracks);

    const handleAddChart = async () => {
        // ensure a track is selected
        const trackId = assignedTracks?.parentTrackId;
        const editId = assignedTracks?.editId;
        if (!trackId && !editId) {
            showToast("error", "Please select a track first");
            return;
        }
        // derive effective timespan
        const inputValid =
            !timespan1HasError && String(timespan1 || "").trim() !== "";
        const effectiveTimespan = inputValid
            ? String(Number(timespan1))
            : String(timespan);
        // validate rank
        if (rankHasError || String(rank || "").trim() === "") {
            showToast("error", "Please enter a valid rank (1-50)");
            return;
        }
        const numericRank = Number(rank);
        //console.log("editd", editId)
        try {
            const result = await doAddTrackToChart({
                trackId,
                editId,
                rank: numericRank,
                timespan: effectiveTimespan,
            });
            if (result?.success) {
                showToast(
                    "success",
                    `Track assigned to rank ${numericRank} for ${effectiveTimespan}-day chart`
                );

                setTimespan1("");
                setAssignedTracks(null);
                resetRank();

                const genre =
                    selectedGenre.name === "All Genres" ? "" : selectedGenre.name;
                const refresh = await doFetchChartManipulations({
                    timespan: effectiveTimespan,
                    genre,
                });
                if (refresh?.success) {
                    const list = Array.isArray(refresh?.response?.data)
                        ? refresh.response.data
                        : [];
                    const assignedTrackIdss = list
                        .map((m) => ({
                            trackId: m?.trackId?._id || m?.trackId,
                            editId: m?.editId?._id || m?.editId,
                        }))
                        .filter((item) => item.trackId && item.editId);
                    setAssignedTrackIds(assignedTrackIdss);
                    const idMap = {};
                    list.forEach((m) => {
                        const tId = m?.trackId?._id || m?.trackId;
                        const eId = m?.editId?._id || m?.editId;
                        const mId = m?._id;
                        if (tId && eId && mId) idMap[`${tId}:${eId}`] = mId;
                    });
                    setManipulationByTrack(idMap);
                }
                fetchChartss();
            } else {
                const msg = result?.error?.message || "Failed to assign track to chart";
                showToast("error", msg);
            }
        } catch (e) {
            showToast("error", e?.message || "Failed to assign track to chart");
        }
    };

    return (
        <div className="px-6 py-10 text-white min-h-screen">
            <div className="bg-[#111111D6] border border-[#505050] p-6 rounded-lg mb-8">
                <h2 className="text-xl font-bold font-['Public_Sans'] mb-4 text-[#E3E3E3]">
                    Chart Manipulation
                </h2>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 xl:flex-row xl:gap-5 xl:h-[70px] items-start xl:items-end">
                        <div className="flex-1 w-full">
                            <CountryDropdown label="Timespan" isRequired options={Timespan} placeholder="Select Timespan" displayKey="name" idKey="name" wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] w-full min-w-[281px]" dropdownClassName="bg-[#2D2D2D] border-[#808080] border z-20" buttonClassName="h-[45px] border-[#808080]" optionClassName="text-[#E2E2E2] py-2" filterClassName="border-t border-[#505050]" labelStyle="font-['Public_Sans'] font-medium text-[#E3E3E3] whitespace-nowrap" placeholderClassName="text-[#808080] text-[14px] font-['Public Sans'] font-normal" isSearchable={true} allowClear={false} defaultOption={Timespan.find((c) => c.name === timespan1)} onSelect={(option) => setTimespan1(option?.name || "")} />
                        </div>

                        <div className="flex-1 w-full">
                            <Input
                                label="Rank"
                                isRequired
                                placeholder="Enter Rank No (1-50)"
                                value={rank}
                                onChange={handleRankChange}
                                onBlur={handleRankBlur}
                                errorMessage={rankErrorMessage}
                                inputStyle="bg-[#45444433] border-[1px] border-[#808080] h-[45px]"
                                outerContainerStyle="mt-[-1px]"
                                labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
                                placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
                            />
                        </div>
                    </div>

                    <div className="flex justify-between items-center w-full gap-4">
                        {/* Assign Existing Track */}
                        <div className="flex items-center gap-3">
                            <CustomButton
                                className="h-[40px] text-[18px] font-normal font-inter"
                                onClick={() => {
                                    setShowTrackSelector(true);
                                }}
                                disabled={
                                    timespan1HasError ||
                                    rankHasError ||
                                    String(timespan1 || "").trim() === "" ||
                                    String(rank || "").trim() === ""
                                }
                            >
                                + Assign Existing Track
                            </CustomButton>

                            {assignedTracks?._id && (
                                <div className="flex items-center gap-3 px-3 py-2 bg-[#1A1A1A] border border-[#505050] rounded-md">
                                    <div className="flex flex-col">
                                        <span className="text-sm text-[#E2E2E2] font-semibold">
                                            {assignedTracks.trackTitle} {assignedTracks?.editTitle
                                                ? `(${assignedTracks.editTitle || "Original"})`
                                                : ""}
                                        </span>
                                        <span className="text-xs text-[#B6B6B6]">
                                            {assignedTracks.artists || "Unknown Artist"}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setAssignedTracks(null)}
                                        className="text-[#C3C3C3] hover:text-white"
                                        aria-label="Remove selected track"
                                        title="Remove selected track"
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M18 6L6 18"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M6 6L18 18"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Add Chart button */}
                        <CustomButton
                            useGradientBorder
                            rounded="rounded-lg"
                            textSize="text-[18px] leading-[24px]"
                            padding="py-[10.5px] px-[13.5px]"
                            font="font-public-sans"
                            border="border-lg"
                            type="button"
                            width="w-[131px]"
                            onClick={handleAddChart}
                            disabled={
                                timespan1HasError ||
                                rankHasError ||
                                !timespan1 ||
                                !rank ||
                                !assignedTracks?._id
                            }
                            loading={isAddingTrack}
                        >
                            Add Chart
                        </CustomButton>
                    </div>
                </div>
            </div>



            <main className="page duration-1000">
                {/* Chart title and options */}
                <div className="page-section pb-[100px]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center w-full mb-3">
                        {/* Mobile row under the title */}
                        {/* Mobile view controls */}
                        <div className="flex flex-row justify-between items-end w-full sm:hidden ">
                            {/* Genre Dropdown to the left */}
                            <div className="w-[60%] max-w-[260px] mr-[8px]">
                                <DropDown
                                    showItem={
                                        <div className="flex flex-row justify-start items-center gap-2">
                                            <span
                                                className={`bg-genre-${selectedGenre.id} w-[10px] h-[10px] rounded-full mr-2`}
                                            />
                                            <span className="text-tertiary font-medium text-sm transition-all">
                                                {selectedGenre.name}
                                            </span>
                                        </div>
                                    }
                                    limit={null}
                                >
                                    {groupedGenres.map((item, index) => (
                                        <DropdownItem
                                            index={index}
                                            key={index}
                                            selected={item.id === selectedGenre.id}
                                            onClick={() => setSelectedGenre(item)}
                                        >
                                            <div className="flex flex-row justify-start items-center gap-2">
                                                <span
                                                    className={`bg-genre-${item.id} w-[10px] h-[10px] rounded-full mr-2`}
                                                />
                                                <span
                                                    className={`${selectedGenre.id === item.id
                                                        ? "text-secondary"
                                                        : "text-tertiary"
                                                        } group-hover/dropdownitem:text-secondary font-medium transition-colors whitespace-normal break-words `}
                                                >
                                                    {item.name}
                                                </span>
                                            </div>
                                        </DropdownItem>
                                    ))}
                                </DropDown>
                            </div>

                            {/* Timespan to the right */}
                            <div className="flex flex-col items-end">
                                <p className="text-sm text-tertiary text-right mr-1">
                                    Timespan
                                </p>
                                <div className="flex gap-[4px] sm:gap-1 h-[30px]sm:h-[40px]">
                                    <ButtonCollection>
                                        {TIMESPANS.map((item, index) => (
                                            <ButtonOfCollection
                                                key={index}
                                                text={item}
                                                isActive={item === timespan}
                                                onClick={() => handleTimespanChange(item)}
                                                classNames="w-[30px] h-[30px]"
                                            />
                                        ))}
                                    </ButtonCollection>
                                </div>
                            </div>
                        </div>

                        {/* Desktop layout (md and up) */}
                        <div className="hidden sm:flex sm:flex-col w-full">
                            {/* Row 2: Filters aligned to end */}
                            <div className="w-full flex justify-end items-center gap-4 -mt-6">
                                <p className="text-sm text-tertiary">Timespan</p>
                                <div className="block h-[40px] w-fit">
                                    <ButtonCollection>
                                        {TIMESPANS.map((item, index) => (
                                            <ButtonOfCollection
                                                key={index}
                                                text={item}
                                                isActive={item === timespan}
                                                onClick={() => handleTimespanChange(item)}
                                                classNames="w-[30px] h-[30px]"
                                            />
                                        ))}
                                    </ButtonCollection>
                                </div>

                               
                            </div>
                        </div>

                        {/* </SectionTitle> */}
                    </div>
                    {/* </SectionTitle> */}
                </div>

                {/* Chart list */}
                <div className="page-section">
                    <div className="flex flex-col">
                        {loading ? (
                            <div
                                className="py-10 text-center text-[#B6B6B6] text-sm"
                                role="status"
                                aria-live="polite"
                            >
                                Loading Tracks…
                            </div>
                        ) : chartTracks?.length === 0 ? (
                            <div className="py-10 text-center text-[#B6B6B6] text-sm">
                                No tracks found for this timespan/genre.
                            </div>
                        ) : (
                            <>
                                <div ref={trackListRef} />
                                {chartTracks.slice(0, 50).map((track, index) => {
                                    if (track.Error) return null;
                                    const firstEditRemixInfo = (track.editInfos || [])[0] || {};
                                    return (
                                        <TrackRow
                                            trackMetadata={{ ...track, coverArt: track?.coverArt?.url }}
                                            key={track.trackId || index}
                                            mainArtist={track.artists}
                                            trackData = {track}
                                            remixMetadata={firstEditRemixInfo}
                                            trackStrength={{
                                                danceability: track.danceability,
                                                energy: track.energy,
                                                valence: track.valence,
                                                popularity: track.popularity,
                                            }}
                                            number={index + 1}
                                            thisPlaylistName="charts"
                                            zValue={index + 1}
                                            openedTrack={openedTrack}
                                            setOpenedTrack={setOpenedTrack}
                                            playlistTracks={
                                                index === 0
                                                    ? chartTracks.slice(0, 50)
                                                    : chartTracks.slice(0, 50).slice(index).concat(
                                                        chartTracks.slice(0, 50).slice(0, index)
                                                    )
                                            }
                                            isManipulated={track.isManipulated}
                                            onDelete={() =>
                                                handleRemoveFromChart(track._id, firstEditRemixInfo.id)
                                            }
                                            rowIndex={index}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>

                {/* Track Modal */}
                {showTrackSelector && (
                    <TrackChartSelectorModal
                        visible={showTrackSelector}
                        onClose={() => setShowTrackSelector(false)}
                        onSelect={handleTrackSelect}
                        onLoadPage={handleLoadTrackPage}
                        onSearch={handleSearchTracks}
                        title="Assign Track to Chart"
                        excludeIds={assignedTrackIds}
                        mode="playlist"
                    />
                )}
            </main>
        </div>
    );
};

export default Charts;